import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertTriangle, Bell, BellOff, Book, Car, CheckCheck, CheckCircle2, ClipboardList, Package,
} from 'lucide-react';

import api from '@/lib/api';
import { EmptyState, ScreenLoader, TopBar } from '@/components/phone/primitives';
import { Colors } from '@/components/phone/theme';

/**
 * The inbox. Mirrors `mobile-frontend/app/notifications.tsx`.
 */

const TYPE_ICONS = {
  order_update: ClipboardList, stage_complete: CheckCircle2, stage_completed: CheckCircle2,
  quality_flag: AlertTriangle, delivery_assigned: Car, order_delivered: CheckCheck,
  delivery_complete: CheckCheck, new_job: Package, ready_for_dispatch: Package,
  binder_complete: Book, default: Bell,
};

const TYPE_COLORS = {
  order_update: Colors.accent.DEFAULT, stage_complete: Colors.status.success,
  stage_completed: Colors.status.success, quality_flag: Colors.status.error,
  delivery_assigned: Colors.status.info, order_delivered: Colors.status.success,
  delivery_complete: Colors.status.success, new_job: Colors.status.info,
  ready_for_dispatch: Colors.status.info, binder_complete: Colors.accent.light,
  default: Colors.text.disabled,
};

/** "12m ago" / "3h ago" / "2d ago". */
function formatAge(created) {
  const mins = Math.floor((Date.now() - new Date(created).getTime()) / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function SupervisorNotificationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications', { params: { page: 1, limit: 50 } });
      setNotifications(res.data.notifications || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load notifications');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const markRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) =>
        ((n.id === id || n.notification_id === id) ? { ...n, read: true } : n)));
    } catch { /* the row is read either way next time the list loads */ }
  };

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not mark them read');
    }
  };

  const hasUnread = notifications.some((n) => !n.read);

  return (
    <div className="flex flex-1 flex-col">
      <TopBar eyebrow="INBOX" title={t('notifications.title')} onBack={() => navigate('/supervisor')} />

      {!loading && hasUnread && (
        <button
          type="button"
          data-testid="mark-all-read-btn"
          onClick={markAllRead}
          className="mx-5 mb-3 mt-3 flex items-center justify-center gap-1.5 rounded-[10px] border py-2.5 text-[13px] font-semibold"
          style={{
            backgroundColor: Colors.accent.wash,
            borderColor: '#CFDEE8',
            color: Colors.accent.DEFAULT,
          }}
        >
          <CheckCheck className="h-4 w-4" /> Mark all as read
        </button>
      )}

      {loading ? <ScreenLoader /> : notifications.length === 0 ? (
        <EmptyState icon={BellOff} message={t('notifications.no_notifications')} />
      ) : (
        <ul className="px-5 pb-8">
          {notifications.map((item) => {
            const Icon = TYPE_ICONS[item.type] || TYPE_ICONS.default;
            const colour = TYPE_COLORS[item.type] || TYPE_COLORS.default;
            const id = item.id || item.notification_id || '';
            const entityId = item.entity_id || item.related_order_id;
            return (
              <li key={id}>
                <button
                  type="button"
                  data-testid={`notif-${id}`}
                  onClick={() => {
                    if (!item.read) markRead(id);
                    if (entityId && item.entity_type === 'order') navigate(`/supervisor/order/${entityId}`);
                  }}
                  className="mt-3 flex w-full items-start gap-4 rounded-[18px] border p-4 text-left"
                  style={{
                    backgroundColor: Colors.background.primary,
                    borderColor: Colors.border.default,
                    borderLeftWidth: item.read ? 1 : 3,
                    borderLeftColor: item.read ? Colors.border.default : Colors.accent.DEFAULT,
                  }}
                >
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
                    style={{ backgroundColor: `${colour}18` }}
                  >
                    <Icon className="h-5 w-5" style={{ color: colour }} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className="block text-[15px]"
                      style={{ color: Colors.text.primary, fontWeight: item.read ? 600 : 700 }}
                    >
                      {item.title || item.type?.replace(/_/g, ' ')}
                    </span>
                    <span className="mt-1 line-clamp-2 block text-[13px] leading-6" style={{ color: Colors.text.secondary }}>
                      {item.message}
                    </span>
                    <span className="mt-2 block text-[11px]" style={{ color: Colors.text.disabled }}>
                      {formatAge(item.created_at)}
                    </span>
                  </span>
                  {!item.read && (
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: colour }} />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
